<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import Icon from 'next/icon/Icon.vue';
import {
  SPACE_TIERS,
  TIER_ICONS,
  TIER_COLORS,
  normalizeTier,
} from '../../pricing/pricingTiers';

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
  allowNone: {
    type: Boolean,
    default: true,
  },
  label: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const open = ref(false);

const selected = computed(() => {
  const value = normalizeTier(props.modelValue);
  if (!value || value === 'none') return null;
  return SPACE_TIERS.includes(value) ? value : null;
});

const options = computed(() => {
  const tiers = SPACE_TIERS.map(tier => ({
    value: tier,
    label: t(`PRICING.TIERS.${tier.toUpperCase()}`),
    icon: TIER_ICONS[tier],
    color: TIER_COLORS[tier],
  }));
  if (props.allowNone) {
    return [
      {
        value: null,
        label: t('PRICING.TIERS.NONE'),
        icon: null,
        color: null,
      },
      ...tiers,
    ];
  }
  return tiers;
});

const selectedOption = computed(
  () =>
    options.value.find(option => option.value === selected.value) ||
    options.value[0]
);

const select = value => {
  emit('update:modelValue', value || null);
  open.value = false;
};

const toggle = () => {
  open.value = !open.value;
};
</script>

<template>
  <div
    v-on-click-outside="() => (open = false)"
    class="relative"
    :class="
      label
        ? 'flex group flex-col gap-1 rounded-lg px-2 py-1.5 transition-colors is-editable hover:bg-n-slate-2 dark:hover:bg-n-solid-1 cursor-pointer'
        : ''
    "
    @click="label ? toggle() : undefined"
  >
    <div v-if="label" class="flex items-center gap-2 min-w-0">
      <p
        class="font-medium text-sm text-slate-900 dark:text-white truncate !m-0"
      >
        {{ label }}
      </p>
    </div>

    <button
      type="button"
      class="flex w-full items-center gap-2 text-left text-sm transition-colors"
      :class="
        label
          ? 'h-9 rounded border border-transparent px-2 hover:bg-n-slate-2 dark:hover:bg-n-solid-2'
          : 'rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-2 hover:bg-n-alpha-1'
      "
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click.stop="toggle"
    >
      <img
        v-if="selectedOption.icon"
        :src="selectedOption.icon"
        :alt="selectedOption.label"
        class="size-6 shrink-0 rounded-full"
      />
      <span
        v-else
        class="flex size-6 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-10"
      >
        <Icon icon="i-lucide-circle-dashed" class="size-3.5" />
      </span>
      <span class="min-w-0 flex-1 truncate text-n-slate-12">
        {{ selectedOption.label }}
      </span>
      <Icon
        icon="i-lucide-chevron-down"
        class="size-4 shrink-0 text-n-slate-10"
        :class="{ 'rotate-180': open }"
      />
    </button>

    <div
      v-if="open"
      class="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-2 shadow-lg"
      role="listbox"
      @click.stop
    >
      <button
        v-for="option in options"
        :key="option.value || 'none'"
        type="button"
        class="flex w-full items-center gap-2 px-2.5 py-2 text-left text-sm transition-colors hover:bg-n-alpha-1"
        :class="{ 'bg-n-alpha-2': option.value === selected }"
        role="option"
        :aria-selected="option.value === selected"
        @click="select(option.value)"
      >
        <img
          v-if="option.icon"
          :src="option.icon"
          :alt="option.label"
          class="size-6 shrink-0 rounded-full"
        />
        <span
          v-else
          class="flex size-6 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-10"
        >
          <Icon icon="i-lucide-circle-dashed" class="size-3.5" />
        </span>
        <span class="truncate text-n-slate-12">{{ option.label }}</span>
        <span
          v-if="option.color"
          class="ml-auto size-2.5 shrink-0 rounded-full"
          :style="{ backgroundColor: option.color }"
        />
      </button>
    </div>
  </div>
</template>
